import { ApolloClient, ApolloLink, from, split } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import authService from "./components/api-authorization/AuthorizeService";
import { ApplicationPaths } from "./components/api-authorization/ApiAuthorizationConstants";
import { cache } from "./utils/cache";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { createUploadLink } from "apollo-upload-client";

type Props = {
  children: JSX.Element;
};

// Guard against re-entrant redirects: once we decide to redirect, don't trigger again.
let redirectingToLogin = false;

const redirectToLogin = async () => {
  if (redirectingToLogin) return;
  // Only redirect if the user WAS logged in (had a stored session) but the server
  // is now rejecting the token — i.e. the token expired/was revoked.
  // If the user was never logged in, AuthorizeRoute handles the redirect instead.
  //
  // Check the actual storage (not the cached profile) — getAccessToken() already
  // reads from storage and checks expiry, so we use it as the source of truth.
  const token = await authService.getAccessToken();
  if (!token) {
    // No valid token in storage. The addAccessTokenExpired handler and
    // AuthorizeRoute will handle the redirect — don't double-redirect.
    return;
  }
  // Token IS present but server rejected it — force re-auth.
  redirectingToLogin = true;
  authService.userManager
    ?.removeUser()
    .catch(() => {})
    .finally(() => {
      window.location.replace(ApplicationPaths.Login);
    });
};

const ApolloConnection = ({ children }: Props) => {
  const httpLink = createUploadLink({
    uri: "/graphql",
  }) as unknown as ApolloLink; // There is a mismatch in ApolloLink types! cast to unknown as a quick fix for now

  const authLink = setContext(async (_, { headers }) => {
    // get the authentication token from authService
    const token = await authService.getAccessToken();
    // return the headers to the context so httpLink can read them
    // IMPORTANT: omit the Authorization header entirely when there is no
    // token — sending an empty "Authorization:" header can cause the server
    // to return AUTH_NOT_AUTHENTICATED instead of treating it as anonymous.
    return {
      headers: {
        ...headers,
        "GraphQL-Preflight": "1",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

        if (extensions?.code === "AUTH_NOT_AUTHENTICATED") {
          console.log("[AUTH_NOT_AUTHENTICATED] — redirecting to login");
          redirectToLogin();
        }

        // Unauthorized (wrong role) — go home, not to login, to avoid loop
        if (extensions?.code === "AUTH_NOT_AUTHORIZED") {
          console.log("[AUTH_NOT_AUTHORIZED] — redirecting to /");
          window.location.replace("/");
        }
      });
    }

    if (networkError) console.log(`[Network error]: ${networkError}`);
  });

  const { protocol, host } = window.location;
  const localUrl = `${protocol === "https:" ? "wss:" : "ws:"}//${host}`;
  const wsLink = new GraphQLWsLink(
    createClient({
      url: localUrl + "/graphql",
      lazy: true,
      connectionParams: async () => {
        const token = await authService.getAccessToken();
        return {
          AuthToken: token,
        };
      },
    }),
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === "OperationDefinition" && definition.operation === "subscription";
    },
    wsLink,
    httpLink,
  );

  const client = new ApolloClient({
    //@ts-ignore
    link: from([errorLink, authLink, splitLink]),
    cache: cache,
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloConnection;
